import React, { useContext } from "react";
import Button from "components/button/button";
import CustomCheckbox from "components/checkbox/custom-checkbox";
import { useTranslation } from "react-i18next";
import { SubscriptionContext } from "context/subscription";
import UpgradePremium from "components/subscription/upgrade-premium";

/**
 * Dedicated "QR Codes" step of the resume builder.
 * These toggles used to live at the bottom of the Milestones panel where they
 * looked randomly placed; they now have their own tab (see Asana MCF task
 * "Resume builder: move QR codes to their own tab").
 */
export default function QRCodePanel({
  onNext,
  onBack,
  onChangeToggleQRCode,
  show_qr_milestone = true,
  show_qr_reels = true,
  hasShowreel = false,
}) {
  const { t } = useTranslation();
  const { isSubscribed } = useContext(SubscriptionContext);

  return (
    <div className="w-full h-full border-r border-resumeBorder">
      <div
        className="w-full overflow-y-auto overflow-x-hidden custom-scroll"
        style={{ height: "calc(100vh - 174px)" }}>
        <div className="w-full px-2 2sm:px-15.5 pb-10.6 pt-5">
          {/* heading */}
          <div className="w-full flex flex-row gap-x-2 items-center border-b border-resumeBorder pb-4">
            <h1 className="text-lightBlack text-lg 2sm:text-xl font-semibold">
              {t("showHideQRCodes")}
            </h1>
            <span
              title={t("qrCodesTabIntro")}
              className="w-5 h-5 rounded-full border border-passwordGray text-passwordGray text-xs flex items-center justify-center cursor-help select-none">
              ?
            </span>
          </div>

          {/* intro */}
          <p className="text-sm text-secondary mt-4 max-w-lg">
            {t("qrCodesTabIntro")}
          </p>

          {/* options */}
          <ul className="mt-2">
            <li className="w-full flex py-4 border-b border-resumeBorder">
              <CustomCheckbox
                containerStyle="pt-2"
                name="show_qr_milestone"
                checked={show_qr_milestone}
                onChange={(e) => {
                  let value = e.target.checked;
                  onChangeToggleQRCode(value, "show_qr_milestone");
                }}
              />
              <div>
                <p className="text-lg font-semibold">
                  {t("qrcodeforMilestonesPageProfile")}
                </p>
                <p className="text-sm">{t("qrcodeforMilestonesDesc")}</p>
              </div>
            </li>

            <li className="w-full flex py-4">
              {isSubscribed ? (
                hasShowreel ? (
                  <>
                    <CustomCheckbox
                      containerStyle="pt-2"
                      name="show_qr_reels"
                      checked={show_qr_reels}
                      onChange={(e) => {
                        let value = e.target.checked;
                        onChangeToggleQRCode(value, "show_qr_reels");
                      }}
                    />
                    <div>
                      <p className="text-lg font-semibold">
                        {t("qrCodeForShowreel")}
                      </p>
                      <p className="text-sm">{t("qrCodeForShowreelDesc")}</p>
                    </div>
                  </>
                ) : (
                  /* No showreel yet: show the option greyed out and explain how
                     to enable it, so we never generate a QR pointing to an empty
                     showreel (Marker.io MCF-1742). */
                  <div className="flex opacity-50 select-none">
                    <CustomCheckbox
                      containerStyle="pt-2 pointer-events-none"
                      name="show_qr_reels"
                      checked={false}
                      onChange={() => {}}
                    />
                    <div>
                      <p className="text-lg font-semibold">
                        {t("qrCodeForShowreel")}
                      </p>
                      <p className="text-sm">{t("qrCodeForShowreelDesc")}</p>
                      <p className="text-xs text-secondary mt-1">
                        Add a video and mark it as your showreel to enable this QR code.
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <UpgradePremium
                  styleProp="list"
                  customHeadline="QR Code - Upgrade to Unlock"
                  customText="Upgrade so you can share QR code for showreel"
                />
              )}
            </li>
          </ul>
        </div>
      </div>
      {/* footer */}
      <div className="w-full py-4 px-6 border-t border-resumeBorder">
        <div className="w-full flex justify-center 2sm:justify-end space-x-5">
          <Button
            buttonName={t("back")}
            buttonTextClass="text-sm font-semibold text-lightBlack"
            buttonRadius="6px"
            buttonClass="bg-thBg px-12 py-3 2sm:px-17.5 2sm:py-5"
            onClick={onBack}
          />
          <Button
            buttonName={t("next")}
            buttonTextClass="text-sm font-semibold text-white"
            buttonRadius="6px"
            buttonClass="bg-primary px-12 py-3 2sm:px-17.5 2sm:py-5"
            onClick={onNext}
          />
        </div>
      </div>
    </div>
  );
}
